import { linkOptions } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { Fragment } from 'react';
import { MdMenu } from 'react-icons/md';
import { PiArrowUpRight } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { PostHogEvent } from '@_/constants';
import { useDialog } from '@_/hooks';
import { userCommonI18n } from '@_/i18n-common';
import {
  Avatar,
  Badge,
  Menu,
  MenuTrigger,
  MenuItemDescription,
  MenuSeparator,
  MenuHeader,
  MenuItemIcon,
  MenuItem,
  MenuItemLabel,
  MenuPopup,
  MenuGroup,
  Link,
  Skeleton,
  SkeletonAvatar,
  Text,
  VanillaLink
} from '@_/ui';
import { isTruthy } from '@_/utils';
import { DesktopNavItem } from '#src/components/AppShell/AppHeader/DesktopNavItem.tsx';
import { LoginDialog } from '#src/components/dialogs/LoginDialog.tsx';
import { buttonI18n, navI18n } from '#src/i18nMessages/index.ts';
import { useStores } from '#src/state/StoreContext.tsx';

export function DesktopUserNav() {
  const posthog = usePostHog();
  const { useAuthStore } = useStores();
  const isAuthReady = useAuthStore(state => !!state.lastUpdated);
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);

  const loginDialog = useDialog({
    component: LoginDialog
  });

  const groupedNavItems = user
    ? [
        user?.hasStaffAccounts && [
          {
            id: 'org-dashboard',
            label: (
              <FormattedMessage id="nav.business-dashboard" defaultMessage="Business dashboard" />
            ),
            href: user.orgDashboardUrl || '/',
            iconEnd: <PiArrowUpRight className="size-4" />
          }
        ],
        [
          {
            id: 'bookings',
            label: <FormattedMessage {...navI18n.bookings} />,
            link: linkOptions({
              from: '/{-$locale}',
              to: '/{-$locale}/bookings'
            })
          },
          {
            id: 'organizations',
            label: <FormattedMessage {...navI18n.saved} />,
            link: linkOptions({
              from: '/{-$locale}',
              to: '/{-$locale}/saved'
            })
          }
        ],
        [
          {
            id: 'settings',
            label: <FormattedMessage {...navI18n.accountSettings} />,
            link: linkOptions({
              from: '/{-$locale}',
              to: '/{-$locale}/settings'
            })
          },
          {
            id: 'logout',
            label: <FormattedMessage {...buttonI18n.logOut} />,
            onClick: (event: React.MouseEvent) => {
              event.preventDefault();
              void logout();
            }
          }
        ]
      ].filter(isTruthy)
    : [
        [
          {
            id: 'register-business',
            label: (
              <FormattedMessage
                id="button.register-your-business"
                defaultMessage="Register your business"
              />
            ),
            description: (
              <span className="block max-w-48 wrap-break-word">
                <FormattedMessage
                  id="button.register-your-business.info"
                  defaultMessage="Reach more customers with our full suite of tools"
                />
              </span>
            ),
            link: linkOptions({
              from: '/{-$locale}',
              to: '/{-$locale}/business'
            })
          }
        ],
        [
          {
            id: 'login',
            label: <FormattedMessage {...buttonI18n.logIn} />,
            onClick: () => {
              loginDialog.open();
            }
          },
          {
            id: 'sign-up',
            label: <FormattedMessage {...buttonI18n.signUp} />,
            onClick: () => {
              posthog.capture(PostHogEvent.SignUpButtonClicked);
              loginDialog.open();
            }
          }
        ]
      ];

  if (!isAuthReady) {
    return (
      <Skeleton>
        <SkeletonAvatar className="size-8" />
      </Skeleton>
    );
  }

  return (
    <>
      {loginDialog.element}

      <Menu>
        {user ? (
          <MenuTrigger className="block size-8" render={<button />}>
            <Avatar className="size-full" name={user?.name} src={user?.avatarUrl} />
          </MenuTrigger>
        ) : (
          <MenuTrigger nativeButton={false} render={<div />}>
            <DesktopNavItem icon={<MdMenu />} subtle />
          </MenuTrigger>
        )}

        <MenuPopup align="end" className="min-w-56" side="bottom">
          {!!user && (
            <>
              <MenuHeader>
                <div className="flex items-center py-1">
                  <div className="flex-none">
                    <Avatar className="size-8" name={user?.name} src={user?.avatarUrl} />
                  </div>

                  <div className="ml-3 flex-auto overflow-hidden">
                    <div className="flex items-center overflow-hidden">
                      <Text block className="truncate" semibold>
                        {user?.name}
                      </Text>

                      {!!user?.parentUserId && (
                        <div className="ml-1.5">
                          <Badge color="primary" size="sm">
                            <FormattedMessage {...userCommonI18n.managedUserShort} />
                          </Badge>
                        </div>
                      )}
                    </div>

                    <Text block className="mt-0.5 truncate" size="xs" subdued>
                      {user?.email}
                    </Text>
                  </div>
                </div>
              </MenuHeader>
              <MenuSeparator />
            </>
          )}

          {groupedNavItems.map((items, groupIndex) => (
            <Fragment key={items.map(item => item.id).join(',')}>
              {groupIndex > 0 && <MenuSeparator />}
              <MenuGroup>
                {items.map(item => (
                  <MenuItem
                    key={item.id}
                    renderLink={props => {
                      if ('link' in item && item.link) {
                        return <Link {...props} {...item.link} />;
                      }

                      if ('href' in item) {
                        return <VanillaLink {...props} href={item.href} target="_blank" />;
                      }
                    }}
                    onClick={'onClick' in item ? item.onClick : undefined}
                  >
                    <MenuItemLabel>{item.label}</MenuItemLabel>

                    {'description' in item && (
                      <MenuItemDescription>{item.description}</MenuItemDescription>
                    )}

                    {'iconEnd' in item && (
                      <MenuItemIcon placement="end">{item.iconEnd}</MenuItemIcon>
                    )}
                  </MenuItem>
                ))}
              </MenuGroup>
            </Fragment>
          ))}
        </MenuPopup>
      </Menu>
    </>
  );
}
